import React, { useEffect, useMemo, useState } from 'react';
import { AlertTriangle, CheckCircle2, ExternalLink, Loader2, Search, Landmark, PlusCircle, Ban, ChevronRight, MessageSquare, Sparkles } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { listTaxCollectors, resolveCorrectionRequest } from '../../utils/taxCollectorsApi';

/** PA-COLLECTOR-AUTOMATION-001 P2 — compact "Resolve collector" panel for pa_auto_assign review items. */
const TYPE_FOR = { county: 'county_treasurer', city: 'municipal_collector', town: 'municipal_collector', township: 'municipal_collector', borough: 'municipal_collector', village: 'municipal_collector', school_district: 'school_collector' };
const CAT_LABEL = { county: 'County Tax', municipal: 'Municipal Tax', school: 'School Tax' };
const norm = (v) => String(v || '').toUpperCase().replace(/[^A-Z0-9]+/g, ' ').trim();

const TONES = { slate: 'bg-slate-100 text-slate-700', emerald: 'bg-emerald-100 text-emerald-700', indigo: 'bg-indigo-100 text-indigo-700', amber: 'bg-amber-100 text-amber-800' };
const Pill = ({ children, tone = 'slate', title }) => (
  <span title={title} className={`inline-flex items-center rounded-full px-1.5 py-[1px] text-[10px] font-semibold uppercase tracking-wide ${TONES[tone]}`}>{children}</span>
);

export const CollectorResolvePanel = ({ row, onResolved }) => {
  const ar = row.auto_resolution || {};
  const jurType = (row.jurisdiction?.jurisdiction_type || row.jurisdiction_type || '').toLowerCase();
  const CAT_TYPE = { county: 'county_treasurer', municipal: 'municipal_collector', school: 'school_collector' };
  const expectedType = TYPE_FOR[jurType] || CAT_TYPE[row.tax_category_code] || null;
  const submittedPayee = row.suggested_collector_payee || ar.submitted_payee || '';
  const [mode, setMode] = useState('use_existing');
  const [selected, setSelected] = useState(ar.candidates?.[0]?.collector_id || '');
  const [search, setSearch] = useState('');
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [newC, setNewC] = useState({ display_name: row.suggested_collector_name || '', make_checks_payable_to: submittedPayee, phone: row.suggested_collector_phone || '', physical_address: row.suggested_collector_address || '', mailing_address: row.suggested_collector_address || '' });
  const [ack, setAck] = useState(false);
  const [note, setNote] = useState('');
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [conflict, setConflict] = useState(null);
  const [dup, setDup] = useState(null);
  const [done, setDone] = useState(null);

  useEffect(() => {
    if (mode !== 'use_existing') return undefined;
    const t = setTimeout(async () => {
      setSearching(true);
      try {
        const d = await listTaxCollectors({ include_counts: false, state: 'PA', active_only: true, page_size: 25, ...(expectedType ? { collector_type: expectedType } : {}), ...(search.trim() ? { search: search.trim() } : {}) });
        setResults(d?.items || d?.collectors || []);
      } catch { setResults([]); } finally { setSearching(false); }
    }, 250);
    return () => clearTimeout(t);
  }, [search, mode, expectedType]);

  const candidates = useMemo(() => {
    const byId = new Map();
    (ar.candidates || []).forEach((c) => byId.set(c.collector_id, { id: c.collector_id, display_name: c.display_name, signals: c.signals || [], collector_type: c.collector_type, make_checks_payable_to: c.make_checks_payable_to ?? results.find((r) => r.id === c.collector_id)?.make_checks_payable_to }));
    results.forEach((r) => { if (!byId.has(r.id)) byId.set(r.id, { ...r, signals: [] }); });
    return [...byId.values()];
  }, [ar.candidates, results]);

  const chosen = candidates.find((c) => c.id === selected);
  const livePayee = mode === 'use_existing' ? chosen?.make_checks_payable_to : newC.make_checks_payable_to;
  const payeeDiffers = !!submittedPayee && livePayee !== undefined && norm(livePayee) !== norm(submittedPayee);

  const canSubmit = !busy && (mode === 'reject' ? reason.trim().length > 0 : mode === 'use_existing' ? !!selected && (!payeeDiffers || ack) : newC.display_name.trim().length >= 2 && newC.make_checks_payable_to.trim().length > 0 && (!payeeDiffers || ack));

  const submit = async () => {
    setBusy(true); setConflict(null); setDup(null);
    try {
      const body = mode === 'reject' ? { action: 'reject', reason: reason.trim() }
        : mode === 'use_existing' ? { action: 'use_existing', collector_id: selected, acknowledge_payee_conflict: ack, note: note.trim() || undefined }
        : { action: 'create_new', acknowledge_payee_conflict: ack, note: note.trim() || undefined, new_collector: { ...newC, collector_type: expectedType, states_served: ['PA'], legal_name: newC.display_name, accepts_mail_away: true } };
      const res = await resolveCorrectionRequest(row.id, body);
      setDone(res);
      toast.success(mode === 'reject' ? 'Review item rejected' : `Assignment ${res.resolution?.assignment_reused ? 'confirmed' : 'created'} · ${res.collector?.display_name}`);
      onResolved?.(res);
    } catch (err) {
      const d = err?.response?.data?.detail;
      if (d?.code === 'payee_conflict') setConflict(d);
      else if (d?.code === 'duplicate_collector') setDup(d);
      else toast.error(typeof d === 'string' ? d : d?.message || 'Could not resolve');
    } finally { setBusy(false); }
  };

  if (done) {
    const r = done.resolution;
    return (
      <section className="ccr-done text-sm" data-testid="ccr-resolve-done">
        <div className="flex items-center gap-2 font-semibold"><CheckCircle2 className="h-4 w-4" />{r ? 'Assignment recorded and review item closed' : 'Review item rejected'}</div>
        {r && (
          <div className="flex flex-wrap gap-x-4 gap-y-1 text-[12px]">
            <span>Collector: <strong>{done.collector?.display_name}</strong>{r.collector_created && <Pill tone="emerald">new</Pill>}</span>
            <span>Assignment: <code className="text-[11px]">{r.assignment_id?.slice(0, 8)}</code>{r.assignment_reused && <Pill tone="slate">existing</Pill>}</span>
            <a className="inline-flex items-center gap-1 underline" href={`/admin/jurisdictions?focus=${row.jurisdiction_id}&tab=assignments`} target="_blank" rel="noreferrer" data-testid="ccr-resolve-open-assignments">Collectors &amp; Assignments <ExternalLink className="h-3 w-3" /></a>
          </div>
        )}
      </section>
    );
  }

  const TABS = [['use_existing', 'Use existing collector', Landmark], ['create_new', 'Create new collector', PlusCircle], ['reject', 'Reject', Ban]];

  return (
    <section className="flex flex-col gap-2.5 text-sm" data-testid="ccr-resolve-panel">
      <div className="flex items-center justify-between gap-2">
        <div className="ccr-section-title">
          Resolve collector
          <span className="ccr-chip ccr-chip-indigo">{CAT_LABEL[row.tax_category_code] || row.tax_category_code || 'Current'}</span>
          <span className="ccr-chip ccr-chip-slate">{row.tax_period || 'current'}</span>
        </div>
        <span className="sr-only" data-testid="ccr-resolve-submitted-payee">{submittedPayee}</span>
        {ar.request_ids?.[0] && (
          <a className="inline-flex items-center gap-1 text-[11.5px] font-medium text-indigo-700 hover:underline" href={`/admin/jurisdictions/requests?focus=${ar.request_ids[0]}`} target="_blank" rel="noreferrer" data-testid="ccr-resolve-open-request">JR-{ar.request_ids[0].slice(0, 8).toUpperCase()} <ExternalLink className="h-3 w-3" /></a>
        )}
      </div>

      <div className="ccr-tabs" role="tablist">
        {TABS.map(([k, l, Icon]) => (
          <button key={k} type="button" role="tab" aria-selected={mode === k} onClick={() => { setMode(k); setAck(false); setConflict(null); setDup(null); }} className="ccr-tab" data-testid={`ccr-resolve-mode-${k}`}>
            <Icon /><span>{l}</span>
          </button>
        ))}
      </div>

      {mode === 'use_existing' && (
        <div className="ccr-pane" key="use_existing">
          <div className="ccr-search">
            <Search />
            <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder={`Search PA ${expectedType ? expectedType.replace('_', ' ') + 's' : 'collectors'}…`} data-testid="ccr-resolve-search" />
          </div>
          <div className="ccr-cards" data-testid="ccr-resolve-candidates">
            {searching && candidates.length === 0 && <div className="flex items-center gap-1 px-1 text-[12px] text-slate-500"><Loader2 className="h-3 w-3 animate-spin" /> Searching…</div>}
            {!searching && candidates.length === 0 && <div className="px-1 text-[12px] text-slate-500">No PA collectors of the expected type found — create a new one.</div>}
            {candidates.map((c) => {
              const differs = !!submittedPayee && c.make_checks_payable_to !== undefined && norm(c.make_checks_payable_to) !== norm(submittedPayee);
              const isTpa = c.collector_type === 'third_party_agency';
              return (
                <label key={c.id} className={`ccr-card ${selected === c.id ? 'is-selected' : ''} ${isTpa ? 'is-tpa' : ''}`} data-testid={`ccr-resolve-candidate-${c.id}`}>
                  <input type="radio" name="ccr-candidate" checked={selected === c.id} onChange={() => { setSelected(c.id); setAck(false); setConflict(null); }} />
                  <span className="ccr-card-icon"><Landmark /></span>
                  <div className="min-w-0 flex-1">
                    <div className="ccr-card-name">{c.display_name}{c.signals.map((s) => <Pill key={s} tone="emerald">{s}</Pill>)}{isTpa && <Pill tone="amber" title={`Confirm it collects ${CAT_LABEL[row.tax_category_code] || 'this category'} here`}>third-party</Pill>}</div>
                    <div className="ccr-card-payee">Payee: {c.make_checks_payable_to || '—'}{differs && <span className="ccr-differs"><AlertTriangle className="h-3 w-3" /> differs</span>}</div>
                  </div>
                  <ChevronRight className="ccr-card-chevron h-4 w-4" />
                </label>
              );
            })}
          </div>
        </div>
      )}

      {mode === 'create_new' && (
        <div className="ccr-pane ccr-form-grid" key="create_new" data-testid="ccr-resolve-create-form">
          {[['display_name', 'Collector name'], ['make_checks_payable_to', 'Make checks payable to'], ['phone', 'Phone'], ['physical_address', 'Physical address'], ['mailing_address', 'Mailing address']].map(([k, l]) => (
            <div key={k} className={k.includes('address') ? 'is-wide' : ''}>
              <div className="ccr-form-label">{l}</div>
              <Input value={newC[k]} onChange={(e) => setNewC((p) => ({ ...p, [k]: k === 'make_checks_payable_to' ? e.target.value.toUpperCase() : e.target.value }))} data-testid={`ccr-resolve-new-${k}`} />
            </div>
          ))}
          {dup && (
            <div className="ccr-conflict is-wide" data-testid="ccr-resolve-duplicate">
              <div>A collector with this name and type already exists{dup.existing_collector ? ` (${dup.existing_collector.display_name})` : ''}.</div>
              {dup.existing_collector && <button type="button" className="self-start font-semibold underline" onClick={() => { setMode('use_existing'); setSelected(dup.existing_collector.id); setSearch(dup.existing_collector.display_name); setDup(null); }} data-testid="ccr-resolve-use-duplicate">Use that collector instead</button>}
            </div>
          )}
        </div>
      )}

      {mode === 'reject' && (
        <div className="ccr-pane" key="reject">
          <Textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={2} placeholder="Reason (required)" className="rounded-xl text-[13px]" data-testid="ccr-resolve-reject-reason" />
        </div>
      )}

      {mode !== 'reject' && (payeeDiffers || conflict) && (
        <div className="ccr-conflict" data-testid="ccr-resolve-conflict">
          <div className="flex items-start gap-1.5"><AlertTriangle className="mt-0.5 h-3.5 w-3.5 flex-shrink-0" /><div>Payee differs: submitted <strong>{submittedPayee}</strong> · collector <strong>{conflict?.collector_payee ?? livePayee}</strong>. The collector's master payee is not changed.</div></div>
          <label><input type="checkbox" checked={ack} onChange={(e) => setAck(e.target.checked)} data-testid="ccr-resolve-ack" /> I verified this collector's payee is correct for this jurisdiction</label>
        </div>
      )}

      <div className="ccr-footer">
        {mode !== 'reject' ? (
          <div className="ccr-note"><MessageSquare /><Textarea value={note} onChange={(e) => setNote(e.target.value)} rows={1} placeholder="Add a note (optional)…" data-testid="ccr-resolve-note" /></div>
        ) : <div className="flex-1" />}
        <Button disabled={!canSubmit} onClick={submit} className={`ccr-cta ${mode === 'reject' ? 'is-reject' : ''}`} data-testid="ccr-resolve-submit">
          {busy ? <><Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> Working…</> : mode === 'reject' ? <><Ban className="mr-1.5 h-4 w-4" /> Reject review item</> : <><Sparkles className="mr-1.5 h-4 w-4" /> Resolve &amp; create assignment</>}
        </Button>
      </div>
    </section>
  );
};

export default CollectorResolvePanel;
